import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Snackbar, Typography,
} from '@mui/material';
import { AutoAwesome as SparkleIcon } from '@mui/icons-material';
import { ConnectorInterestUser } from '../../super-picker/dialogs/others/SuperConnectorInterestDialog';
import DistinctiveDialog from '../../super-picker/dialogs/others/DistinctiveDialog';
import { DISTINCTIVE_TEMPLATES } from '../../super-picker/distinctive/distinctiveTemplates';
import { useReportBottomBannerHeight } from '../hooks/useReportBottomBannerHeight';

interface DemoBannerProps {
  user: ConnectorInterestUser;
  /** False when a granted user is only previewing Demo. Hides register-interest copy. */
  showInterest: boolean;
}

const SUPER_CONNECTOR_TEMPLATES = DISTINCTIVE_TEMPLATES.filter(
  (template) => template.id === 'super-connector',
);

/**
 * Bottom banner shown while the app is in demo mode. Lets a prospect register
 * their interest after sharing/downloading the personalized Super Connector EDM,
 * and surfaces a prompt whenever a demo user attempts a blocked action (the api
 * layer dispatches `sc-demo-blocked`).
 */
export default function DemoBanner({ user, showInterest }: DemoBannerProps) {
  const [edmOpen, setEdmOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [registered, setRegistered] = useState(false);
  const bannerRef = useReportBottomBannerHeight(8);

  // Surface the "this is a demo" prompt whenever a blocked action is attempted.
  useEffect(() => {
    const handler = () => setPromptOpen(true);
    window.addEventListener('sc-demo-blocked', handler);
    return () => window.removeEventListener('sc-demo-blocked', handler);
  }, []);

  const openInterest = () => { setPromptOpen(false); setEdmOpen(true); };

  return (
    <>
      {/* Fixed bottom banner */}
      <Box
        ref={bannerRef}
        sx={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: (t) => t.zIndex.appBar + 1,
          bgcolor: '#0f172a', color: '#f8fafc',
          borderTop: '2px solid #ed8936',
          px: { xs: 2, md: 4 }, py: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap',
          boxShadow: '0 -6px 20px rgba(0,0,0,0.25)',
          animation: 'demoBannerPulse 1.8s ease-in-out infinite',
          '@keyframes demoBannerPulse': {
            '0%, 100%': {
              backgroundColor: '#0f172a',
              boxShadow: '0 -6px 20px rgba(0,0,0,0.25)',
            },
            '50%': {
              backgroundColor: '#172554',
              boxShadow: '0 -8px 30px rgba(237,137,54,0.6)',
            },
          },
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SparkleIcon sx={{ color: '#ed8936' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#f8fafc' }}>
            You're exploring a demo of Super Connector — the data above is sample data.
          </Typography>
        </Box>
        {showInterest && (
          <Button
            variant="contained"
            size="small"
            onClick={openInterest}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none', fontWeight: 700 }}
          >
            Register Interest
          </Button>
        )}
      </Box>

      {/* "This is a demo" prompt when a blocked action is attempted */}
      <Snackbar
        open={promptOpen}
        autoHideDuration={6000}
        onClose={() => setPromptOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 'calc(var(--sc-bottom-banner-height, 80px) + 8px)', md: 'calc(var(--sc-bottom-banner-height, 80px) + 8px)' } }}
      >
        <Alert
          severity="info"
          onClose={() => setPromptOpen(false)}
          action={showInterest
            ? <Button color="inherit" size="small" onClick={openInterest}>Register Interest</Button>
            : undefined}
          sx={{ alignItems: 'center' }}
        >
          {showInterest
            ? 'This is a demo. Register your interest to unlock full access.'
            : 'This is a demo.'}
        </Alert>
      </Snackbar>

      {/* Share/download the personalized EDM before Register Interest unlocks. */}
      <DistinctiveDialog
        open={edmOpen}
        onClose={() => setEdmOpen(false)}
        user={user}
        templates={SUPER_CONNECTOR_TEMPLATES}
        title="Super Connector"
        description="Download or share your personalized EDM to unlock Register Interest."
        values={{
          agentLine: [user.name, user.cea, user.mobile || user.email]
            .map((part) => part.trim())
            .filter(Boolean)
            .join(' · '),
          agencyLine: 'Huttons Asia Pte Ltd · L3008899K',
        }}
        onRegistered={() => setRegistered(true)}
      />
      <Snackbar open={registered} autoHideDuration={5000} onClose={() => setRegistered(false)}>
        <Alert severity="success" onClose={() => setRegistered(false)}>
          Interest registered — we'll see you at your appointment!
        </Alert>
      </Snackbar>

    </>
  );
}
